import { useEffect, useMemo, useRef, useState } from "react";
import { moderationApi, type ReasonCategory } from "../../lib/moderation-api";
import type { Parcel } from "../../viewer/types";
import { ReviewQueue } from "./ReviewQueue";
import { ReviewClients } from "./ReviewClients";
import { TerritoryInspector } from "./TerritoryInspector";
import { ParcelModerationNote } from "./ParcelModerationNote";
import { cn } from "../cn";

/**
 * Review Operations workspace: CENTER (Review Queue OR Clients + Territory
 * selection) and RIGHT (Territory Inspector / bare-parcel note). Owns the
 * current selection and the ordered id list that powers Previous / Next
 * operational traversal. A mutation in the Inspector bumps the shared refresh
 * tick and the pending counter.
 *
 * When a client is opened in the Clients view the left column WIDENS to host the
 * embedded interactive Grid (Admin Clients — Macro #20), keeping the Inspector
 * on the right so a parcel click flows straight into moderation.
 */
export function AdminReviewOperations({
  view,
  onDataChanged,
}: {
  view: "queue" | "clients";
  onDataChanged: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  const [tick, setTick] = useState(0);
  const [reasons, setReasons] = useState<ReasonCategory[]>([]);
  const listRef = useRef<(ids: string[]) => void>(() => {});

  useEffect(() => {
    const c = new AbortController();
    moderationApi.reasonCategories(c.signal).then((r) => setReasons(r.reason_categories)).catch(() => {});
    return () => c.abort();
  }, []);

  const onList = useMemo(() => {
    const fn = (ids: string[]) => setOrderedIds(ids);
    listRef.current = fn;
    return fn;
  }, []);

  const idx = selectedId ? orderedIds.indexOf(selectedId) : -1;
  const hasPrev = idx > 0;
  const hasNext = idx >= 0 && idx < orderedIds.length - 1;

  const bump = () => {
    setTick((t) => t + 1);
    onDataChanged();
  };

  // Selecting a Territory (queue row, client Territory row, or a parcel that
  // maps to a Territory) opens the Inspector; a bare parcel opens the note.
  const selectTerritory = (id: string) => { setSelectedId(id); setSelectedParcel(null); };
  const selectBareParcel = (p: Parcel) => { setSelectedParcel(p); setSelectedId(null); };
  const openClient = (owner: string | null) => {
    setSelectedOwner(owner);
    setSelectedId(null);
    setSelectedParcel(null);
  };

  const wideLeft = view === "clients" && !!selectedOwner;

  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-5 items-start",
        wideLeft
          ? "xl:grid-cols-[minmax(0,1fr)_minmax(0,440px)]"
          : "xl:grid-cols-[minmax(0,440px)_minmax(0,1fr)]",
      )}
    >
      <div>
        {view === "queue" ? (
          <ReviewQueue tick={tick} selectedId={selectedId} onSelect={selectTerritory} onList={onList} />
        ) : (
          <ReviewClients
            tick={tick}
            owner={selectedOwner}
            onOpenClient={openClient}
            selectedId={selectedId}
            onSelect={selectTerritory}
            onSelectBareParcel={selectBareParcel}
            onList={onList}
          />
        )}
      </div>
      {selectedParcel ? (
        <ParcelModerationNote parcel={selectedParcel} />
      ) : (
        <TerritoryInspector
          key={selectedId ?? "none"}
          identityId={selectedId}
          reasons={reasons}
          onMutated={bump}
          onPrev={() => { if (hasPrev) setSelectedId(orderedIds[idx - 1]); }}
          onNext={() => { if (hasNext) setSelectedId(orderedIds[idx + 1]); }}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />
      )}
    </div>
  );
}
